// LearnCore Frontend - 文言文材料编辑页面

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import TagEditor from '../components/TagEditor';
import Loading from '../components/Loading';
import ClassicalContentEditor from '../components/ClassicalContentEditor';
import type { ClassicalPassageWithDetails } from '../types';
import {
  BookOpenIcon,
  ArrowLeftIcon,
  SparklesIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

export default function EditClassicalChinesePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [passage, setPassage] = useState<ClassicalPassageWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // 表单状态
  const [title, setTitle] = useState('');
  const [originalText, setOriginalText] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

  useEffect(() => {
    if (id) {
      loadPassage();
    }
  }, [id]);

  const loadPassage = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getClassicalPassage(id!);
      const passageData = response.passage;
      setPassage(passageData);
      setTitle(passageData.title);
      setOriginalText(passageData.original_text);
      setIsPublic(passageData.is_public === 1);
      
      // 加载标签
      try {
        const tagsResponse = await apiClient.getClassicalPassageTags(id!);
        const tagIds = tagsResponse.tags.map(tag => tag.id);
        setSelectedTagIds(tagIds);
      } catch (tagErr) {
        console.error('加载标签失败:', tagErr);
        // 标签加载失败不阻塞页面加载
      }
    } catch (err: any) {
      setError(err.response?.data?.error || '获取文言文材料失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setError('请输入标题');
      return;
    }

    if (!originalText.trim()) {
      setError('请输入文言文原文');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await apiClient.updateClassicalPassage(id!, {
        title: title.trim(),
        original_text: originalText.trim(),
        is_public: isPublic,
        tag_ids: selectedTagIds,
      });

      setSuccessMessage('保存成功');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || '保存失败，请稍后重试');
    } finally {
      setSaving(false);
    }
  };

  const handleExtract = async () => {
    try {
      setSaving(true);
      setError(null);
      await apiClient.triggerClassicalExtraction(id!);
      setSuccessMessage('已触发内容提取，请稍后查看结果');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || '提取失败，请稍后重试');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (error && !passage) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <XMarkIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">加载失败</h3>
          <p className="text-gray-500 mb-6">{error}</p>
          <button
            onClick={() => navigate('/classical-chinese')}
            className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-md inline-flex items-center transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            返回列表
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面头部 */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/classical-chinese')}
              className="text-gray-500 hover:text-gray-700 mr-4"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
                <BookOpenIcon className="h-8 w-8 mr-3 text-amber-600" />
                编辑文言文材料
              </h1>
              <p className="mt-2 text-gray-600">
                修改文言文材料的基本信息
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 成功消息 */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <CheckIcon className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* 错误提示 */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <XMarkIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">操作失败</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
              <div className="mt-4">
                <button
                  onClick={() => setError(null)}
                  className="btn-outline text-sm"
                >
                  关闭
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto">
        {/* 基本信息卡片 */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">基本信息</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              标题 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
              placeholder="例如：论语·学而篇"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              文言文原文 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={originalText}
              onChange={(e) => setOriginalText(e.target.value)}
              rows={12}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500 font-serif"
              placeholder="粘贴或输入文言文原文..."
            />
          </div>

          {/* 选项 */}
          <div className="space-y-4 pt-4 border-t">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
              />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {isPublic ? (
                    <>公开分享</>
                  ) : (
                    <>私有</>
                  )}
                </p>
                <p className="text-xs text-gray-500">
                  允许其他用户查看和学习这份材料
                </p>
              </div>
            </label>
          </div>

          {/* 标签编辑器 */}
          <div className="pt-2 border-t border-gray-200">
            <TagEditor
              passageId={id}
              initialTags={passage?.tags || []}
              onTagsChange={(tagIds) => setSelectedTagIds(tagIds)}
              disabled={saving}
              entityType="classical-passage"
            />
          </div>

          {/* 操作按钮 */}
          <div className="flex justify-between pt-4 border-t">
            <div className="flex space-x-3">
              <button
                onClick={handleSave}
                disabled={saving || !title.trim() || !originalText.trim()}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {saving ? (
                  <>
                    <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    保存中...
                  </>
                ) : (
                  <>
                    <CheckIcon className="h-4 w-4 mr-2" />
                    保存修改
                  </>
                )}
              </button>

              <button
                onClick={handleExtract}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <SparklesIcon className="h-4 w-4 mr-2" />
                重新提取
              </button>
            </div>

            <button
              onClick={() => navigate('/classical-chinese')}
              className="btn-outline"
            >
              取消
            </button>
          </div>
        </div>

        {/* 提取内容编辑器 - 只有在提取完成后才显示 */}
        {passage && passage.processing_status === 'completed' && (
          <div className="mt-8">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">编辑提取内容</h2>
            <ClassicalContentEditor
              key={passage.updated_at} // 使用更新时间作为key，确保数据变化时组件重新渲染
              passage={passage}
              passageId={id!}
              onUpdate={loadPassage}
            />
          </div>
        )}

        {/* 说明文字 */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-900 mb-2">💡 编辑说明</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>修改基本信息：</strong>可以修改标题、原文和公开设置</li>
            <li>• <strong>编辑标签：</strong>可以添加、删除或修改标签</li>
            <li>• <strong>编辑提取内容：</strong>如果已完成AI提取，可以编辑译文、词汇、语法等内容</li>
            <li>• <strong>重新提取：</strong>修改原文后，可以点击"重新提取"触发AI分析</li>
            <li>• <strong>保存修改：</strong>只保存基本信息，不会重新触发AI分析</li>
            <li>• 重新提取会覆盖之前的所有AI分析结果</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
